import React from 'react'
import PropTypes from 'prop-types'
import Avatar from 'components/ds/Avatar'
import Icon from 'components/ds/Icon'
import styles from './UtilityBar.module.scss'

const cx = (...p) => p.filter(Boolean).join(' ')

export const UserMenu = ({ name, role, organisation, onAccount, onSignOut, accountLabel, signOutLabel }) => {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef(null)

  React.useEffect(() => {
    if (!open) return undefined
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const run = fn => () => { setOpen(false); if (fn) fn() }

  return (
    <div className={styles.account} ref={ref}>
      <button
        type="button"
        className={styles.accountButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <Avatar name={name} size="medium" />
        <span className={styles.accountName}>{name}</span>
        <Icon name="chevron-down" size={16} />
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.identity}>
            <Avatar name={name} size="large" />
            <span className={styles.identityText}>
              <span className={styles.identityName}>{name}</span>
              {role && <span className={styles.identityMeta}>{role}</span>}
              {organisation && <span className={styles.identityMeta}>{organisation}</span>}
            </span>
          </div>

          <div className={styles.separator} />

          {onAccount && (
            <button type="button" role="menuitem" className={styles.menuItem} onClick={run(onAccount)}>
              <Icon name="user" size={18} />
              {accountLabel}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.signOut)}
            onClick={run(onSignOut)}
          >
            <Icon name="log-out" size={18} />
            {signOutLabel}
          </button>
        </div>
      )}
    </div>
  )
}

UserMenu.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.node,
  organisation: PropTypes.node,
  onAccount: PropTypes.func,
  onSignOut: PropTypes.func.isRequired,
  accountLabel: PropTypes.node,
  signOutLabel: PropTypes.node,
}

UserMenu.defaultProps = { accountLabel: 'Account', signOutLabel: 'Sign out' }

const UtilityBar = ({ onToggleNav, navCollapsed, children, ...rest }) => (
  <header className={styles.bar}>
    <div className={styles.left}>
      <button
        type="button"
        className={styles.navToggle}
        onClick={onToggleNav}
        aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-expanded={!navCollapsed}
      >
        <Icon name="panel-left" size={18} />
      </button>
    </div>
    <div className={styles.right} {...rest}>{children}</div>
  </header>
)

UtilityBar.propTypes = {
  onToggleNav: PropTypes.func.isRequired,
  navCollapsed: PropTypes.bool,
  children: PropTypes.node,
}

export default UtilityBar
